import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Markdown from '../../components/Markdown';
import Page from '../../components/Page';
import RightPane from './components/RightPane';
import Grid from '@material-ui/core/Grid';
import ContentContainer from '../../components/ContentContainer.js';
import AppBar from '@material-ui/core/AppBar';
import { TabPane, a11yProps, StyledTab, StyledTabs } from '../../components/Tabs.js';
import QuoteCard from './components/QuoteCard';
import PersonCard from './components/PersonCard';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import backgroundImg from '../../assets/about-page-background.svg';
import heroImg from '../../assets/about-page-image.svg';

const useStyles = makeStyles(theme => ({
  hero: {
    width: '100%',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    backgroundSize: 'cover',
    backgroundImage: `url(${backgroundImg})`,
  },
  content: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '4rem 1rem',
    [theme.breakpoints.down('sm')]: {
      padding: '4rem 1rem 1rem 1rem',
    },
  },
  title: {
    width: '100%',
    textAlign: 'left',
    marginBottom: '1rem',
    color: theme.palette.common.white,
  },
  text: {
    textAlign: 'left',
    lineHeight: '2rem',
    color: theme.palette.common.highlightBlue,
  },
  imgContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: '1.5rem',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  img: {
    borderRadius: '1rem',
    width: '18rem',
    objectFit: 'contain',
    [theme.breakpoints.down('sm')]: {
      width: '60%',
    },
  },
  section: {
    padding: '3rem 0 5rem 0',
    '& h2': {
      marginBottom: '2rem',
    },
  },
  linkContainer: {
    margin: '5rem',
    lineHeight: '2rem',
  },
  link: {
    textDecoration: 'none',
    color: theme.palette.primary.main,
  },
  appBar: {
    backgroundColor: theme.palette.background.paper,
    border: 'none',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.common.paleGrey}`,
  },
}));

const AboutPage = ({content}) => {
  const classes = useStyles();
  const [value, setValue] = React.useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  if (!content) return null;
  return (
    <Page title={content.title}>
      <div className={classes.hero}>
        <Container maxWidth="lg" fixed disableGutters>
          <Grid container spacing={6}>
            <Grid item md={7}>
              <div className={classes.content}>
                <Typography variant="h1" color="primary" className={classes.title}>
                  The disability services marketplace with a difference.
                </Typography>
                <Typography variant="h3" className={classes.text}>
                  Clickability is changing the way people with disabilities find and manage their NDIS services. We are driven by a mission to reduce the complexity of navigating the NDIS for both providers and participants, through technology that enables true participant choice and self-directed decision making.
                </Typography>
              </div>
            </Grid>
            <Grid item md={5}>
              <div className={classes.imgContainer}>
                <img src={heroImg} alt="reviews" className={classes.img} />
              </div>
            </Grid>
          </Grid>
        </Container>
      </div>
      <ContentContainer>
        <Grid container spacing={6}>
          <Grid item md={7}>
            <div className={classes.section}>
              <Markdown content={content.bodyCommon}/>
            </div>
            <AppBar position="static" className={classes.appBar}>
              <StyledTabs value={value} onChange={handleChange} aria-label="Tabs">
                <StyledTab label="Our mission" aria-label="Mission tab selector" {...a11yProps(0)} />
                <StyledTab label="Our team" aria-label="Team tab selector" {...a11yProps(1)} />
              </StyledTabs>
            </AppBar>
            <TabPane value={value} index={0}>
              <div className={classes.section}>
                <Markdown content={content.bodyMission}/>
                {content.quote.map((q, i) => {
                  return <QuoteCard key={i} author={q.author} content={q.content} />;
                })}
              </div>
            </TabPane>
            <TabPane value={value} index={1}>
              <div className={classes.section}>
                <Markdown content={content.bodyTeam}/>
                {content.person.map((p, i) => {
                  return <PersonCard key={i} name={p.name} position={p.position} description={p.description} image={p.photo}/>;
                })}
              </div>
            </TabPane>
          </Grid>
          <Grid item md={5}>
            <RightPane />
          </Grid>
        </Grid>
      </ContentContainer>
    </Page>
  );
};

AboutPage.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    bodyCommon: PropTypes.string,
    bodyMission: PropTypes.string,
    bodyTeam: PropTypes.string,
    quote: PropTypes.array,
    person: PropTypes.array,
  })
};

export default AboutPage;
